import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { SmilePlus, X } from "lucide-react";
import reactionsService from "../services/reactionsService";
import useCardHoverHold from "../hooks/useCardHoverHold";
import ReactionPicker from "./ReactionPicker";
import ReactorTooltip from "./ReactorTooltip";
import { useResponsive } from "./responsive";
import { useTranslation } from "../i18n";

// Reactions live on their own row on mobile now, so we can show more pills before rolling into "+N".
const MAX_VISIBLE_MOBILE = 2;
const MAX_VISIBLE_DESKTOP = 5;

// Scatter pattern for the particle burst: mostly upward, slight spread.
const PARTICLE_OFFSETS = [
  { dx: -20, dy: -26, rot: -24, delay: 0 },
  { dx: -7, dy: -34, rot: 12, delay: 40 },
  { dx: 7, dy: -30, rot: -10, delay: 20 },
  { dx: 19, dy: -24, rot: 22, delay: 60 },
  { dx: -13, dy: -14, rot: -16, delay: 80 },
  { dx: 14, dy: -12, rot: 18, delay: 50 },
];

// [{key, emoji, count, reacted, reactor_names}] -> { key: {emoji, count, reacted, reactorNames} }
const toReactionMap = (list) => {
  const map = {};
  (list || []).forEach((r) => {
    map[r.key] = { emoji: r.emoji, count: r.count, reacted: r.reacted, reactorNames: r.reactor_names || [] };
  });
  return map;
};

// `onReactionsChange` lets an owner cache the authoritative summary. Callers
// that unmount this bar (e.g. a collapsing comment thread) need it: local
// `reactions` state dies with the unmount, so without the cache the next mount
// would fall back to a stale `initialReactions` and the viewer's own reaction
// would appear to vanish until a page refresh.
const ReactionBar = ({ targetType, targetId, initialReactions, accentColor = "#7c3aed", onReactionsChange }) => {
  const { t } = useTranslation("feed");
  const { isMobile } = useResponsive();
  const maxVisible = isMobile ? MAX_VISIBLE_MOBILE : MAX_VISIBLE_DESKTOP;
  const [reactions, setReactions] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  // Key of the reaction the viewer just added; drives the pop + burst effects.
  const [poppedKey, setPoppedKey] = useState(null);
  const [burst, setBurst] = useState(null);
  // Key of the reaction whose full reactor list is shown in the modal.
  const [modalKey, setModalKey] = useState(null);
  const pickerRef = useRef(null);
  const burstTimer = useRef(null);
  const longPressTimer = useRef(null);
  const longPressFiredRef = useRef(false);

  useEffect(() => setReactions(toReactionMap(initialReactions)), [initialReactions]);
  useEffect(() => () => clearTimeout(burstTimer.current), []);
  useEffect(() => () => clearTimeout(longPressTimer.current), []);

  const handlePressStart = (key) => {
    longPressFiredRef.current = false;
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setModalKey(key);
    }, 450);
  };
  const handlePressCancel = () => {
    clearTimeout(longPressTimer.current);
  };

  const TIP_MAX_NAMES = 5;
  const [hoverTip, setHoverTip] = useState(null);
  const tipCloseTimer = useRef(null);
  useEffect(() => () => clearTimeout(tipCloseTimer.current), []);

  const openTip = (key, el) => {
    clearTimeout(tipCloseTimer.current);
    setHoverTip((prev) => (prev && prev.key === key ? prev : { key, el }));
  };
  
  const scheduleCloseTip = () => {
    clearTimeout(tipCloseTimer.current);
    tipCloseTimer.current = setTimeout(() => setHoverTip(null), 150);
  };
  const closeTip = useCallback(() => setHoverTip(null), []);

  // Keep the feed card's hover lift while a portaled overlay is open.
  useCardHoverHold(hoverTip?.el);
  useCardHoverHold(pickerOpen && !isMobile ? pickerRef.current : null);

  const openPicker = () => {
    if (!catalog) {
      reactionsService.getOptions().then(setCatalog).catch(() => {});
    }
    setPickerOpen((v) => !v);
  };

  const toggleReaction = async (key, emoji) => {
    if (busyKey) return;
    const current = reactions[key];
    const isRemoving = current?.reacted;
    if (!isRemoving) {
      setPoppedKey(key);
      setBurst({ key, id: Date.now() });
      clearTimeout(burstTimer.current);
      burstTimer.current = setTimeout(() => setBurst(null), 800);
    }

    // Optimistic update; the server response is authoritative.
    setReactions((prev) => {
      const next = { ...prev };
      const entry = next[key] || { emoji, count: 0, reacted: false };
      const count = entry.count + (isRemoving ? -1 : 1);
      if (count <= 0) {
        delete next[key];
      } else {
        next[key] = { ...entry, count, reacted: !isRemoving };
      }
      return next;
    });

    setBusyKey(key);
    try {
      const summary = isRemoving
        ? await reactionsService.removeReaction(targetType, targetId, key)
        : await reactionsService.addReaction(targetType, targetId, key);
      setReactions(toReactionMap(summary?.reactions));
      if (onReactionsChange) onReactionsChange(summary?.reactions || []);
    } catch (err) {
      // Revert optimistic change on failure.
      setReactions((prev) => {
        const next = { ...prev };
        if (current) {
          next[key] = current;
        } else {
          delete next[key];
        }
        return next;
      });
    } finally {
      setBusyKey(null);
    }
  };

  const sorted = useMemo(
    () =>
      Object.entries(reactions)
        .map(([key, r]) => ({ key, ...r }))
        .sort((a, b) => b.count - a.count),
    [reactions]
  );
  const visible = sorted.slice(0, maxVisible);
  const overflow = sorted.slice(maxVisible);

  const renderPill = ({ key, emoji, count, reacted }) => (
    <button
      key={key}
      type="button"
      aria-pressed={reacted}
      onClick={() => {
        if (longPressFiredRef.current) {
          longPressFiredRef.current = false;
          return;
        }
        toggleReaction(key, emoji);
      }}
      onPointerDown={(e) => {
        if (e.pointerType !== "mouse") handlePressStart(key);
      }}
      onPointerUp={handlePressCancel}
      onPointerLeave={handlePressCancel}
      onPointerCancel={handlePressCancel}
      onMouseEnter={(e) => {
        if (!isMobile) openTip(key, e.currentTarget);
      }}
      onMouseLeave={() => {
        if (!isMobile) scheduleCloseTip();
      }}
      onContextMenu={(e) => e.preventDefault()}
      className={`relative inline-flex items-center gap-1 min-h-[30px] px-2.5 py-1 rounded-full border-2 bg-white text-sm transition-all duration-150 cursor-pointer select-none ${
        reacted
          ? "font-medium scale-[1.02]"
          : "border-gray-300 text-gray-600 hover:border-gray-400"
      }`}
      style={{
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        ...(reacted
          ? {
              borderColor: accentColor,
              background: "#ffffff",
              color: accentColor,
            }
          : undefined),
      }}
    >
      <span
        aria-hidden="true"
        className={`text-base leading-none ${poppedKey === key ? "reaction-pop" : ""}`}
        onAnimationEnd={() => setPoppedKey(null)}
      >
        {emoji}
      </span>
      {count > 0 && <span className="text-xs font-semibold tabular-nums leading-none">{count}</span>}
      {burst?.key === key &&
        PARTICLE_OFFSETS.map((p, i) => (
          <span
            key={`${burst.id}-${i}`}
            aria-hidden="true"
            className="reaction-particle"
            style={{
              "--dx": `${p.dx}px`,
              "--dy": `${p.dy}px`,
              "--rot": `${p.rot}deg`,
              animationDelay: `${p.delay}ms`,
            }}
          >
            {emoji}
          </span>
        ))}
    </button>
  );

  const tipReaction = hoverTip ? reactions[hoverTip.key] || null : null;
  const ReactorTip = !isMobile &&
    tipReaction &&
    (tipReaction.reactorNames || []).length > 0 && (
      <ReactorTooltip
        anchorEl={hoverTip.el}
        reaction={tipReaction}
        maxNames={TIP_MAX_NAMES}
        onSeeAll={() => {
          setModalKey(hoverTip.key);
          setHoverTip(null);
        }}
        onMouseEnter={() => clearTimeout(tipCloseTimer.current)}
        onMouseLeave={scheduleCloseTip}
        onRequestClose={closeTip}
      />
    );

  const modalReaction = modalKey ? (reactions[modalKey] || null) : null;
  const backdropNoCalloutStyle = {
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    userSelect: "none",
  };
  const ReactorModal = modalReaction && createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 select-none"
      onClick={() => setModalKey(null)}
      onContextMenu={(e) => e.preventDefault()}
      style={backdropNoCalloutStyle}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[70vh] flex flex-col select-text"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.stopPropagation()}
        style={{
          WebkitTouchCallout: "default",
          WebkitUserSelect: "text",
          userSelect: "text",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{modalReaction.emoji}</span>
            <div>
              <p className="font-semibold text-gray-900 text-sm">
                {t("reactions.reacted", { count: modalReaction.count })}
              </p>
              <p className="text-xs text-gray-400">{modalKey.replace(/_/g, " ")}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setModalKey(null)}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul className="overflow-y-auto py-2 px-3 space-y-1">
          {(modalReaction.reactorNames || []).map((name, i) => (
            <li key={i} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-gray-50">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ background: accentColor }}
              >
                {name[0]?.toUpperCase() ?? "?"}
              </div>
              <span className="text-sm text-gray-800">{name}</span>
            </li>
          ))}
          {(modalReaction.reactorNames || []).length === 0 && (
            <li className="px-2 py-3 text-sm text-gray-400 text-center">{t("reactions.no_names")}</li>
          )}
        </ul>
      </div>
    </div>,
    document.body
  );

  const OverflowModal = overflowOpen && createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4 menu-backdrop-in"
      onClick={() => setOverflowOpen(false)}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-sm max-h-[75vh] flex flex-col menu-sheet-in sm:menu-pop-in"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
      >
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <p className="font-semibold text-gray-900 text-sm">{t("reactions.more_title")}</p>
            <p className="text-xs text-gray-400">
              {t("reactions.more_subtitle", { count: overflow.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOverflowOpen(false)}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500"
            aria-label={t("reactions.close")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul className="overflow-y-auto py-2 px-3 space-y-2">
          {overflow.map(({ key, emoji, count, reacted, reactorNames }) => (
            <li key={key}>
              <button
                type="button"
                onClick={() => {
                  setOverflowOpen(false);
                  setModalKey(key);
                }}
                className="w-full text-left rounded-xl border border-gray-100 hover:bg-gray-50 active:bg-gray-100 px-3 py-2.5 transition-colors"
                style={
                  reacted
                    ? { borderColor: `${accentColor}66`, background: `${accentColor}0d` }
                    : undefined
                }
              >
                <div className="flex items-center gap-2.5">
                  <span className="text-xl leading-none">{emoji}</span>
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">{count}</span>
                  <span className="text-xs text-gray-400 capitalize truncate">
                    {key.replace(/_/g, " ")}
                  </span>
                </div>
                {(reactorNames || []).length > 0 ? (
                  <p className="mt-1.5 text-xs text-gray-500 line-clamp-2">
                    {(reactorNames || []).join(", ")}
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-gray-400">{t("reactions.tap_to_see")}</p>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body
  );

  return (
    <div className="relative flex w-full items-center gap-1 flex-nowrap justify-start">
      {ReactorModal}
      {OverflowModal}
      {ReactorTip}
      {visible.map(renderPill)}

      {overflow.length > 0 && (
        <button
          type="button"
          aria-label={t("reactions.more_aria", { count: overflow.length })}
          aria-expanded={overflowOpen}
          onClick={() => {
            if (longPressFiredRef.current) {
              longPressFiredRef.current = false;
              return;
            }
            setOverflowOpen(true);
          }}
          onPointerDown={(e) => {
            if (e.pointerType !== "mouse") {
              longPressFiredRef.current = false;
              clearTimeout(longPressTimer.current);
              longPressTimer.current = setTimeout(() => {
                longPressFiredRef.current = true;
                setOverflowOpen(true);
              }, 450);
            }
          }}
          onPointerUp={() => clearTimeout(longPressTimer.current)}
          onPointerLeave={() => clearTimeout(longPressTimer.current)}
          onPointerCancel={() => clearTimeout(longPressTimer.current)}
          onContextMenu={(e) => e.preventDefault()}
          className="inline-flex items-center min-h-[30px] px-2.5 py-1 rounded-full border-2 border-gray-300 bg-white text-gray-500 text-xs font-semibold hover:border-gray-400 transition-colors flex-shrink-0 select-none"
          style={{
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          +{overflow.length}
        </button>
      )}

      <div className="relative flex-shrink-0" ref={pickerRef}>
        <button
          type="button"
          title={t("reactions.add")}
          aria-label={t("reactions.add")}
          aria-expanded={pickerOpen}
          onClick={openPicker}
          className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-full border-2 border-dashed border-gray-300 bg-white text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors"
        >
          <SmilePlus className="w-4 h-4" />
        </button>

        {pickerOpen && (
          <ReactionPicker
            catalog={catalog}
            reactions={reactions}
            anchorRef={pickerRef}
            onClose={() => setPickerOpen(false)}
            onSelect={(key, emoji) => {
              toggleReaction(key, emoji);
              setPickerOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ReactionBar;
